import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
} from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  sendTealiumEvent,
  EnrollmentPages,
  getValidationObject,
  limitInputLength,
} from "../../../helpers";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import {
  Choice,
  Alert,
  ChoiceList,
  Dropdown,
  TextField,
} from "@cmsgov/ds-medicare-gov";
import PlanInfo, { getPlanTypeKey } from "./../PlanInfo";
import states from "./../states";
import PhoneNumberInput from "./../PhoneNumberInput";
import {
  PlanCategoryType,
  PlanInfoNeededForEnrollment,
  PlanType,
} from "../../../@types";
import { removeExtraWhiteSpace } from "../../../helpers/enrollmentFormHelpers";

const PlanTypeSepcificAgreement: FunctionComponent<{
  plan: PlanInfoNeededForEnrollment;
}> = ({ plan }) => {
  if (plan.category === PlanCategoryType.PFFS) {
    return (
      <>
        <p id="agreement-pffs-info">
          <FormattedHTMLMessage
            id="enroll_form.agreement.pffs.info"
            values={{ plan_name: plan.name }}
          />
        </p>
        <p id="agreement-pffs-pay">
          <FormattedHTMLMessage
            id="enroll_form.agreement.pffs.pay_info"
            values={{ plan_name: plan.name }}
          />
        </p>
        <p id="agreement-pffs-provider">
          <FormattedHTMLMessage
            id="enroll_form.agreement.contract_info.pffs.provider_info"
            values={{ plan_name: plan.name }}
          />
        </p>
      </>
    );
  }
  if (plan.plan_type === PlanType.PDP) {
    return (
      <p id="agreement-pdp">
        <FormattedHTMLMessage
          id="enroll_form.agreement.contract_info.pdp"
          values={{ plan_name: plan.name }}
        />
      </p>
    );
  }
  return (
    <p id="agreement-ma">
      <FormattedHTMLMessage
        id="enroll_form.agreement.contract_info.ma"
        values={{ plan_name: plan.name }}
      />
    </p>
  );
};

const Agreement: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  /* istanbul ignore next: built-in functionality */
  const [isValidForm, setIsValidForm] = useState(false);
  /* istanbul ignore next: built-in functionality */
  const [submitted, setSubmitted] = useState(false);
  /* istanbul ignore next: built-in functionality */
  const [
    agreeToContractErrorMessage,
    setAgreeToContractErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    agreeToInformationReleaseErrorMessage,
    setAgreeToInformationReleaseErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [isEnrolleeErrorMessage, setIsEnrolleeErrorMessage] = useState<
    string | undefined
  >(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeNameErrorMessage,
    setauthorizedRepresentativeNameErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeRelationshipErrorMessage,
    setauthorizedRepresentativeRelationshipErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeAddressLine1ErrorMessage,
    setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeCityErrorMessage,
    setauthorizedRepresentativeCityErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeStateErrorMessage,
    setauthorizedRepresentativeStateErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeZipErrorMessage,
    setauthorizedRepresentativeZipErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativePhoneErrorMessage,
    setauthorizedRepresentativePhoneErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const alertDiv = useRef<HTMLDivElement>(null);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.Agreement
    );

    setIsValidForm(formStatus.isValid);

    if (submitted) {
      if (formStatus.agreeToContractValidity) {
        setAgreeToContractErrorMessage(undefined);
      } else {
        setAgreeToContractErrorMessage(
          t("enroll_form.agreement.click_box_to_continue")
        );
      }
      if (formStatus.agreeToInformationReleaseValidity) {
        setAgreeToInformationReleaseErrorMessage(undefined);
      } else {
        setAgreeToInformationReleaseErrorMessage(
          t("enroll_form.agreement.click_box_to_continue")
        );
      }
      if (formStatus.isEnrolleeValidity) {
        setIsEnrolleeErrorMessage(undefined);
      } else {
        setIsEnrolleeErrorMessage(
          t("enroll_form.click_button_to_choose_option")
        );
      }

      if (formStatus.authorizedRepresentativeNameValidity) {
        setauthorizedRepresentativeNameErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeNameErrorMessageErrorMessage(
          t("enroll_form.authorized_representative.name.error")
        );
      }
      if (formStatus.authorizedRepresentativeRelationshipValidity) {
        setauthorizedRepresentativeRelationshipErrorMessageErrorMessage(
          undefined
        );
      } else {
        setauthorizedRepresentativeRelationshipErrorMessageErrorMessage(
          t("enroll_form.authorized_representative.relationship.error")
        );
      }
      if (formStatus.authorizedRepresentativeAddressLine1Validity) {
        setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage(
          undefined
        );
      } else {
        setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage(
          t("enroll_form.address.line_1.error")
        );
      }
      if (formStatus.authorizedRepresentativeCityValidity) {
        setauthorizedRepresentativeCityErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeCityErrorMessageErrorMessage(
          t("enroll_form.address.city.error")
        );
      }
      if (formStatus.authorizedRepresentativeStateValidity) {
        setauthorizedRepresentativeStateErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeStateErrorMessageErrorMessage(
          t("enroll_form.address.state.error")
        );
      }
      if (formStatus.authorizedRepresentativeZipValidity) {
        setauthorizedRepresentativeZipErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeZipErrorMessageErrorMessage(
          t("enroll_form.address.zip.error")
        );
      }
      if (formStatus.authorizedRepresentativePhoneValidity) {
        setauthorizedRepresentativePhoneErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativePhoneErrorMessageErrorMessage(
          t("enroll_form.address.phone.error")
        );
      }
    }
  };

  const submit = (): void => {
    setSubmitted(true);

    if (isValidForm) {
      /* istanbul ignore next: tested elsewhere */
      nextPage();
    } else {
      /* istanbul ignore next: tested elsewhere */
      validateForm();
    }
  };

  useEffect(() => {
    if (submitted && !isValidForm) {
      // istanbul ignore next: not testing
      if (alertDiv.current) {
        // istanbul ignore next: not testing
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  useEffect(() => {
    validateForm();
  }, [enrollData, submitted]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.plan_agreement")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <span className="sr-only">
          {t("sr_only_additional_questions_may_appear")}
        </span>

        <div ref={alertDiv}>
          {!isValidForm && submitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        <section>
          <FormattedHTMLMessage
            id="enroll_form.agreement.chosen_to_enroll_in_plan"
            tagName="h2"
            values={{ plan_name: plan.name }}
          />
          <h3>{t("enroll_form.agreement.please_read")}</h3>
          <h3>
            {t(
              "enroll_form.agreement.by_completing_enrollment_agree_to_following"
            )}
          </h3>
          <PlanTypeSepcificAgreement plan={plan} />
          <FormattedHTMLMessage
            id={
              plan.plan_type === PlanType.PDP
                ? "enroll_form.agreement.service_area_info.pdp"
                : "enroll_form.agreement.service_area_info"
            }
            tagName="p"
            values={{ plan_name: plan.name }}
          />

          {plan.plan_type !== PlanType.PDP && (
            <FormattedHTMLMessage
              id="enroll_form.agreement.get_all_health_care_from_plan"
              tagName="p"
              values={{ plan_name: plan.name }}
            />
          )}
          {plan.plan_type === PlanType.PDP && (
            <p>{t("enroll_form.agreement.late_enrollment_penalty")}</p>
          )}

          <FormattedHTMLMessage
            id="enroll_form.agreement.sales_agent_disclaimer"
            tagName="p"
            values={{ plan_name: plan.name }}
          />
          <p className="ds-u-margin-y--5">
            {agreeToContractErrorMessage && (
              <span
                className="ds-c-field__hint ds-u-color--error"
                role="alert"
                data-testid="agreeToContractError"
              >
                {agreeToContractErrorMessage}
              </span>
            )}
            <Choice
              type="checkbox"
              name="agreeToContract"
              className="questionHeading"
              checked={enrollData.agreeToContract}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                /* istanbul ignore next: tested elsewhere */
                setEnrollData({
                  ...enrollData,
                  agreeToContract: e.target.checked,
                });
              }}
              value="agreed"
            >
              {t("enroll_form.agreement.i_have_read_and_agree")}
            </Choice>
          </p>
        </section>

        <section>
          <h2>{t("enroll_form.agreement.release_of_information")}</h2>
          <FormattedHTMLMessage
            id="enroll_form.agreement.release_of_information_acknowledgement"
            tagName="p"
            values={{
              plan_name: plan.name,
              plan_type: t(getPlanTypeKey(plan)),
            }}
          />
          <p>{t("enroll_form.agreement.signature_disclaimer")}</p>
          <p className="ds-u-margin-y--5">
            {agreeToInformationReleaseErrorMessage && (
              <span className="ds-c-field__hint ds-u-color--error" role="alert">
                {agreeToInformationReleaseErrorMessage}
              </span>
            )}
            <Choice
              type="checkbox"
              name="agreeToInformationRelease"
              className="questionHeading"
              checked={enrollData.agreeToInformationRelease}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                /* istanbul ignore next: tested elsewhere */
                setEnrollData({
                  ...enrollData,
                  agreeToInformationRelease: e.target.checked,
                });
              }}
              value="agreed"
            >
              {t("enroll_form.agreement.i_have_read_and_agree")}
            </Choice>
          </p>

          <ChoiceList
            type="radio"
            choices={[
              {
                label: t(
                  "enroll_form.agreement.i_am_person_on_enrollment_form"
                ),
                value: "yes",
                checked: enrollData.isEnrollee,
              },
              {
                label: t(
                  "enroll_form.agreement.i_am_authorized_to_act_on_behalf"
                ),
                value: "no",
                checked: enrollData.isEnrollee === false,
              },
            ]}
            errorMessage={isEnrolleeErrorMessage}
            label={
              <h3 className="ds-u-text-transform--none">
                {t("enroll_form.agreement.select_relationship_statement")}
              </h3>
            }
            name="isEnrollee"
            onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
              /* istanbul ignore next: built-in functionality */
              const isEnrollee = e.currentTarget.value === "yes";
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent(
                "Plan Enroll - Plan Agreement",
                `Relationship ${
                  isEnrollee
                    ? "person listed on form"
                    : "authorized representative"
                }`
              );
              /* istanbul ignore next: tested elsewhere */
              setEnrollData({
                ...enrollData,
                isEnrollee,
              });
            }}
          />
          {!enrollData.isEnrollee && enrollData.isEnrollee !== undefined && (
            <>
              <TextField
                name="authorizedRepresentativeName"
                label={t("enroll_form.authorized_representative.name")}
                value={enrollData.authorizedRepresentative.name}
                errorMessage={authorizedRepresentativeNameErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      name: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      name: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeRelationship"
                label={t("enroll_form.authorized_representative.relationship")}
                value={enrollData.authorizedRepresentative.relationship}
                errorMessage={authorizedRepresentativeRelationshipErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      relationship: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      relationship: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeAddressLine1"
                label={t("enroll_form.address.line_1")}
                value={enrollData.authorizedRepresentative.addressLine1}
                errorMessage={authorizedRepresentativeAddressLine1ErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      addressLine1: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      addressLine1: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeCity"
                label={t("enroll_form.address.city")}
                value={enrollData.authorizedRepresentative.city}
                errorMessage={authorizedRepresentativeCityErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      city: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      city: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <Dropdown
                options={[{ name: "", fipsCode: "" }, ...states].map(state => {
                  return { label: state.name, value: state.fipsCode };
                })}
                value={
                  enrollData.authorizedRepresentative
                    ? enrollData.authorizedRepresentative.state
                    : ""
                }
                errorMessage={authorizedRepresentativeStateErrorMessage}
                label={t("enroll_form.address.state")}
                name="authorizedRepresentativeState"
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      state: e.currentTarget.value,
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeZip"
                label={t("enroll_form.address.zip")}
                value={enrollData.authorizedRepresentative.zip}
                errorMessage={authorizedRepresentativeZipErrorMessage}
                fieldClassName="ds-c-field--zipCode"
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  limitInputLength("zipCode", 5);
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      zip: e.target.value,
                    },
                  });
                }}
              />
              <PhoneNumberInput
                phoneNumber={enrollData.authorizedRepresentative.phone}
                name="authorizedRepresentativePhone"
                errorMessage={authorizedRepresentativePhoneErrorMessage}
                label={t("enroll_form.phone_number")}
                onChange={(authorizedRepresentativePhone): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      phone: authorizedRepresentativePhone,
                    },
                  });
                }}
              />
            </>
          )}
        </section>

        <BackNextButtons
          inReview={inReview}
          nextPage={submit}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default Agreement;
